import React from 'react';
import { styled } from 'linaria/react';
import { css } from 'linaria';
import { theme } from '../Theme';
import Drawer, { DrawerTarget } from '@jetshop/ui/Modal/Drawer';
import { DrawerTrigger } from '@jetshop/ui/Modal/Drawer/DrawerTrigger';
import useHeaderHeight from '../../hooks/useHeaderHeight';
import Image from '@jetshop/ui/Image';
import { VariantSelector } from './VariantSelector';
import { useProductVariants } from '@jetshop/core/hooks/useProductVariants';
import { useAddMultipleToCart } from '@jetshop/core/hooks/useAddMultipleToCart';
import { useNotification } from '@jetshop/core/components/Notifications';
import addMultipleToCartMutation from '../Cart/addMultipleToCart.gql';
import cartQuery from '../Cart/CartQuery.gql';
import t, { useIntl } from '@jetshop/intl';
import Button from '../ui/Button';
import CartProvider from '@jetshop/core/components/Query/CartProvider';
import { ReactComponent as Cart } from '../../svg/Cart.svg';
import { StyledPrice } from '../CategoryPage/ProductCard';

const toastStyles = css`
  background: white;
  color: ${theme.colors.black};
  padding: 1em;
  line-height: 1.5;
  display: flex;
  align-items: center;
  white-space: normal;

  svg {
    width: 1.5em;
    height: 1.5em;
    margin-right: 0.5em;
  }
`;

const DrawerWrapper = styled('div')`
  > div {
    background: #fff;
    top: ${props => props.topPosition}px;
    height: calc(100vh - ${props => props.topPosition}px);
    display: flex;
    flex-direction: column;
  }
`;

const Flyout = styled('div')`
  background: white;
  color: ${theme.colors.black};
  padding: 16px;
  width: 320px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${theme.below.lg} {
    width: 100%;
  }
`;

const Title = styled('h2')`
  color: #000;
  font-family: brandon-grotesque;
  font-size: 24px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  padding-bottom: 16px;
  flex-shrink: 0;
`;

const ProductList = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow-y: auto;
  flex: 1;

  > div:not(:last-child) {
    border-bottom: 1px solid #dfdfdf;
    padding-bottom: 24px;
    padding-top: 10px;
  }

  > div:last-child {
    padding-top: 10px;
  }
`;

const ProductCard = styled('div')`
  display: flex;
  flex-direction: column;
  /* gap: 16px; */

  .variantsrow-wrapper {
    display: none;
  }

  .product-details-wrapper {
    /* display: none; */
    p {
      margin-bottom: 0;
    }
  }

  .price-wrapper {
    display: flex;
    margin-bottom: 8px;
    gap: 4px;
  }

  .button-variant-select {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin: 10px 0 0 0;
    button {
      height: 36px;
      margin: 0;
    }
  }
`;

const ImageWrapper = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const EmptyImage = styled('div')`
  aspect-ratio: 1;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProductName = styled('p')`
  font-family: ${theme.fonts.primary};
  font-size: 14px;
  margin: 8px 0 4px;
`;

const AddToCartButton = styled(Button)`
  width: 100%;
  background: black;
  height: 40px;
  font-weight: bold;
  font-size: 12px;
  line-height: 12px;
  text-align: center;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #ffffff;
  margin-top: 24px;
`;

const QuickBuyProductCard = ({ product, onVariantSelect }) => {
  const variantHandler = useProductVariants(product, {
    preselectCheapest: false
  });

  const {
    selectedVariant,
    getMissingOptions,
    validationMessage
  } = variantHandler;

  React.useEffect(() => {
    if (selectedVariant) {
      onVariantSelect(product.id, selectedVariant);
    }
  }, [selectedVariant, product.id, onVariantSelect]);

  return (
    <ProductCard key={product.id || Math.random()}>
      {product?.images?.length > 1 ? (
        <ImageWrapper>
          <Image
            src={product?.images[0]?.url}
            aspect="1:1"
            alt={product?.name}
          />
          <Image
            src={product?.images[1]?.url}
            aspect="1:1"
            alt={product?.name}
          />
        </ImageWrapper>
      ) : (
        <ImageWrapper>
          <Image
            src={product?.images[0]?.url}
            aspect="1:1"
            alt={product?.name}
          />
          <EmptyImage />
        </ImageWrapper>
      )}
      <div className="product-details-wrapper">
        <ProductName>{product?.name || ''}</ProductName>
        <div className="price-wrapper">
          <StyledPrice
            price={selectedVariant?.previousPrice || product?.previousPrice}
            className="old-price"
          />
          <StyledPrice
            price={selectedVariant?.price || product?.price}
            className="new-price"
          />
        </div>
      </div>

      {product?.hasVariants && (
        <VariantSelector
          product={product}
          variantHandler={variantHandler}
          showValidation={!!validationMessage}
        />
      )}
    </ProductCard>
  );
};

function Notification({ selectedCount }) {
  const t = useIntl();
  return (
    <div className={toastStyles}>
      <Cart />
      <span>
        {t('{count} products added to cart', { count: selectedCount })}
      </span>
    </div>
  );
}

export const QuickBuyTrigger = ({ children }) => {
  return (
    <DrawerTrigger preventOverflow={true} id="quickbuy-drawer">
      {drawer => {
        const handleClick = () => {
          drawer.showTarget(true);
        };

        return children({
          onClick: handleClick
        });
      }}
    </DrawerTrigger>
  );
};

const QuickBuyFlyoutView = ({ products = [], modal, cart, ...rest }) => {
  const [selectedVariants, setSelectedVariants] = React.useState({});
  const [notify] = useNotification();

  const handleVariantSelect = React.useCallback((productId, variant) => {
    setSelectedVariants(prev => ({
      ...prev,
      [productId]: variant
    }));
  }, []);

  const items = React.useMemo(() => {
    return products
      .filter(product => {
        // Only include products that either:
        // 1. Don't have variants
        // 2. Have a selected variant
        return !product.hasVariants || selectedVariants[product.id];
      })
      .map(product => {
        const variant = selectedVariants[product.id];
        return {
          articleNumber: variant
            ? variant.articleNumber
            : product.articleNumber,
          quantity: 1,
          comments: []
        };
      });
  }, [products, selectedVariants]);

  const [addToCart, { loading }] = useAddMultipleToCart(
    items,
    {
      addMultipleToCartMutation,
      cartQuery
    },
    {
      onCompleted: () => {
        notify(
          <Notification
            productNames={
              items.length === 1
                ? [products[0].name]
                : products.map(p => p.name)
            }
            selectedCount={items.length}
          />
        );
        modal.hideTarget();
      },
      onError: error => {
        console.error('Add to cart error:', error);
        notify(<div>{t('Failed to add products to cart')}</div>);
      }
    }
  );

  // Enable the button if there's at least one valid item to add
  const canAddToCart = items.length > 0;

  return (
    <Flyout {...rest}>
      <Title>Shoppa stilen</Title>
      <ProductList>
        {products.map(product => (
          <QuickBuyProductCard
            key={product.id}
            product={product}
            onVariantSelect={handleVariantSelect}
          />
        ))}
      </ProductList>
      <AddToCartButton onClick={addToCart} disabled={!canAddToCart || loading}>
        {loading
          ? t('Adding...')
          : t('Add selected ({count}) to cart', { count: items.length })}
      </AddToCartButton>
    </Flyout>
  );
};

const QuickBuyFlyout = ({ products, ...props }) => {
  const topPosition = useHeaderHeight();

  return (
    <CartProvider query={cartQuery}>
      {result =>
        result.data && result.data.cart ? (
          <DrawerTarget id="quickbuy-drawer">
            {drawer => (
              <DrawerWrapper topPosition={topPosition}>
                <Drawer isOpen={drawer.isOpen} right>
                  <QuickBuyFlyoutView
                    modal={drawer}
                    products={products}
                    cart={result.data.cart}
                    {...props}
                  />
                </Drawer>
              </DrawerWrapper>
            )}
          </DrawerTarget>
        ) : null
      }
    </CartProvider>
  );
};

export default QuickBuyFlyout;
